import { useState, useEffect } from "react";
import { Plus, Tag, Trash2, Edit2, Award } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBrands({ user }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    brand_name: "",
    points_per_item: 10,
    is_active: true
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await authFetch(`${API}/admin/sponsored-brands`, { });
      if (response.ok) {
        setBrands(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingBrand 
        ? `${API}/admin/sponsored-brands/${editingBrand.brand_id}`
        : `${API}/admin/sponsored-brands`;
      
      const response = await authFetch(url, {
        method: editingBrand ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingBrand ? "Marca aggiornata" : "Marca creata");
        setShowDialog(false);
        setEditingBrand(null);
        resetForm();
        fetchBrands();
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (brandId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa marca?")) return;
    
    try {
      const response = await authFetch(`${API}/admin/sponsored-brands/${brandId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Marca eliminata");
        fetchBrands();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditDialog = (brand) => {
    setEditingBrand(brand);
    setFormData({
      brand_name: brand.brand_name,
      points_per_item: brand.points_per_item,
      is_active: brand.is_active
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      brand_name: "",
      points_per_item: 10,
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-brands-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Marche Sponsorizzate</h1>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditingBrand(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="new-brand-btn" className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Nuova Marca
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">
                {editingBrand ? "Modifica Marca" : "Nuova Marca Sponsorizzata"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="form-group">
                <label className="form-label">Nome Marca *</label>
                <input
                  data-testid="input-brand-name"
                  type="text"
                  className="form-input"
                  value={formData.brand_name}
                  onChange={(e) => setFormData({...formData, brand_name: e.target.value})}
                  placeholder="Es: Bosch, Brembo, Magneti Marelli..."
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Punti per Articolo *</label>
                <input
                  data-testid="input-brand-points"
                  type="number"
                  className="form-input"
                  value={formData.points_per_item}
                  onChange={(e) => setFormData({...formData, points_per_item: parseInt(e.target.value) || 0})}
                  min="1"
                  required
                />
                <p className="text-zinc-500 text-xs mt-1">
                  Punti assegnati per ogni articolo di questa marca nel preventivo
                </p>
              </div>
              <div className="flex items-center justify-between">
                <label className="form-label">Attiva</label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setShowDialog(false)} className="text-zinc-400">
                  Annulla
                </Button>
                <Button data-testid="submit-brand-btn" type="submit" className="btn-primary">
                  {editingBrand ? "Aggiorna" : "Crea Marca"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Banner */}
      <div className="bg-primary/10 border border-primary/20 rounded-sm p-4">
        <div className="flex items-center gap-3">
          <Award className="w-6 h-6 text-primary" />
          <div>
            <p className="text-white font-medium">Sistema Punti</p>
            <p className="text-zinc-400 text-sm">
              Gli utenti guadagnano punti quando creano preventivi con articoli di queste marche.
              I punti vengono calcolati automaticamente in base alla quantità degli articoli.
            </p>
          </div>
        </div>
      </div>

      {/* Brands List */}
      {brands.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((brand) => (
            <div key={brand.brand_id} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-5 card-hover">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Tag className="w-5 h-5 text-primary" />
                  <span className={`px-2 py-0.5 rounded-sm text-xs uppercase ${brand.is_active ? "status-accepted" : "status-cancelled"}`}>
                    {brand.is_active ? "Attiva" : "Inattiva"}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(brand)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(brand.brand_id)}
                    className="text-zinc-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <h3 className="text-xl font-bold text-white">{brand.brand_name}</h3>
              <div className="mt-4 flex items-center gap-2">
                <Award className="w-4 h-4 text-primary" />
                <span className="text-primary font-bold font-['JetBrains_Mono']">{brand.points_per_item}</span>
                <span className="text-zinc-500 text-sm">punti/articolo</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Tag className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">Nessuna marca</p>
          <p className="empty-state-text">Aggiungi marche sponsorizzate per il sistema punti</p>
        </div>
      )}
    </div>
  );
}
